import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSavedMessagesByChannel,
  SAVED_MESSAGES_PAGE_SIZE,
} from '../../store/slices/savedMessageSlice';
import SavedMessageItem from '../savedMessages/SavedMessageItem';
import { BookmarkIcon } from './SidebarIcons';

const ChannelSavedMessagesPanel = ({ channel, onClose, onMessageClick }) => {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const isDirectMessage =
    channel?.type === 'direct' || channel?.type === 'DIRECT_MESSAGE';

  const {
    savedMessagesByChannel,
    channelMetaByChannel,
    channelLoading,
    channelLoadingMore,
    error,
  } = useSelector((state) => state.savedMessages);

  const channelId = channel?.id;
  const savedMessages = channelId
    ? savedMessagesByChannel[channelId] || []
    : [];
  const meta = channelId ? channelMetaByChannel[channelId] : null;
  const hasMore = meta?.hasMore ?? false;
  const totalElements = meta?.totalElements ?? savedMessages.length;

  useEffect(() => {
    if (channelId) {
      dispatch(
        fetchSavedMessagesByChannel({
          channelId,
          page: 0,
          size: SAVED_MESSAGES_PAGE_SIZE,
          append: false,
        })
      );
    }
  }, [channelId, dispatch]);

  const loadMore = useCallback(() => {
    if (!channelId || !hasMore || channelLoadingMore || channelLoading) {
      return;
    }
    const nextPage = (meta?.page ?? 0) + 1;
    dispatch(
      fetchSavedMessagesByChannel({
        channelId,
        page: nextPage,
        size: SAVED_MESSAGES_PAGE_SIZE,
        append: true,
      })
    );
  }, [
    channelId,
    channelLoading,
    channelLoadingMore,
    dispatch,
    hasMore,
    meta?.page,
  ]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 200) {
      loadMore();
    }
  }, [loadMore]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll, savedMessages.length]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
        <div className="flex items-center space-x-2 min-w-0">
          <BookmarkIcon className="w-5 h-5 text-primary-500 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Bookmarks
              {totalElements > 0 ? (
                <span className="ml-1.5 text-sm font-normal text-gray-500 dark:text-gray-400">
                  ({totalElements})
                </span>
              ) : null}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {isDirectMessage
                ? 'Bookmarked in this chat'
                : `Bookmarked in #${channel?.name || ''}`}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Close bookmarks"
          aria-label="Close bookmarks"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-3 min-h-0"
      >
        {channelLoading && savedMessages.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        ) : error && savedMessages.length === 0 ? (
          <div className="text-center py-8 px-4">
            <p className="text-sm text-red-500 mb-3">{error}</p>
            <button
              type="button"
              onClick={() =>
                channelId &&
                dispatch(
                  fetchSavedMessagesByChannel({
                    channelId,
                    page: 0,
                    size: SAVED_MESSAGES_PAGE_SIZE,
                    append: false,
                  })
                )
              }
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Retry
            </button>
          </div>
        ) : savedMessages.length === 0 ? (
          <div className="text-center py-12 px-4">
            <BookmarkIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              No bookmarks in this chat
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Open a message menu and choose Bookmark to keep it here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {savedMessages.map((savedMessage) => (
              <SavedMessageItem
                key={savedMessage.id || savedMessage.messageId}
                savedMessage={savedMessage}
                inChannelPanel
                onMessageClick={onMessageClick}
              />
            ))}
            {channelLoadingMore && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
              </div>
            )}
            {!hasMore && savedMessages.length > 0 && (
              <p className="text-center text-xs text-gray-400 dark:text-gray-500 py-2">
                All bookmarks loaded
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelSavedMessagesPanel;
